import {mapiqApi} from '@lib/infrastructure';
import {withPayloadType} from '@lib/utils';
import {createAction, createAsyncThunk} from '@reduxjs/toolkit';
import {parseISO} from 'date-fns';
import {normalize} from 'normalizr';
import {getSuggestedDesks} from './selectors';
import {AppThunkApiConfig} from '../../createStore';
import {handleError} from '../../infrastructure';
import {Desk, DeskBookingStatus, deskSuggestionEntity} from '../entities';
import {NormalizedDeskSuggestionsResult, SuggestionError} from './types';

export const loadDesks = createAsyncThunk<Desk[], string, AppThunkApiConfig>(
  'desks/loadDesks',
  async (areaId, {extra}) => {
    const client = extra.apiClient;
    const api = mapiqApi(client);
    const response = await api.getDesksByAreaId(areaId);

    return [...response] as Desk[];
  },
);

export const loadDeskSuggestions = createAsyncThunk<
  NormalizedDeskSuggestionsResult,
  {areaId: string; date: string; deskCount?: number; reservationId?: string},
  AppThunkApiConfig
>('desks/loadDeskSuggestions', async ({areaId, date, deskCount, reservationId}, {extra}) => {
  const client = extra.apiClient;
  const api = mapiqApi(client);
  const isoDate = parseISO(date);
  const response = await api.getDeskSuggestions(areaId, isoDate, deskCount, reservationId);

  const normalized = normalize<any, NormalizedDeskSuggestionsResult>(response, deskSuggestionEntity);
  return normalized.entities;
});

export const loadDeskBookingStatus = createAsyncThunk<
  DeskBookingStatus,
  {areaId: string; date: string},
  AppThunkApiConfig
>('desks/loadDeskBookingStatus', async ({areaId, date}, {extra}) => {
  const client = extra.apiClient;
  const api = mapiqApi(client);
  const isoDate = parseISO(date);
  const response = await api.getDeskBookingStatus(areaId, isoDate);

  return {...response};
});

export const loadDeskSuggestionsAndData = createAsyncThunk<
  string[],
  {areaId: string; date: string; deskCount?: number; reservationId?: string},
  AppThunkApiConfig
>(
  'desks/loadSuggestedDesks',
  async ({areaId, date, deskCount, reservationId}, {dispatch, rejectWithValue, getState}) => {
    try {
      await Promise.all([
        dispatch(loadDesks(areaId)).unwrap(),
        dispatch(loadDeskBookingStatus({areaId, date})).unwrap(),
        dispatch(
          loadDeskSuggestions({
            areaId,
            date,
            deskCount,
            reservationId,
          }),
        ).unwrap(),
      ]);

      const newState = getState();
      const suggestedDesks = getSuggestedDesks(newState, deskCount);
      if (!suggestedDesks) {
        dispatch(suggestionErrorSet('NoAvailableDesks'));
        return rejectWithValue('No available desks');
      } else {
        dispatch(clearSuggestionError());
      }
      return suggestedDesks.map((suggestedDesk) => suggestedDesk.id);
    } catch (error) {
      dispatch(suggestionErrorSet('LoadingError'));
      return rejectWithValue('Failed loading desks and suggetions');
    }
  },
);

export const loadDesksByAreaId = createAsyncThunk<
  {desks: Desk[]; bookedById: DeskBookingStatus},
  {areaId: string; date: string},
  AppThunkApiConfig
>('desks/loadDesksByAreaId', async ({areaId, date}, {dispatch, rejectWithValue}) => {
  try {
    const [desks, bookedById] = await Promise.all([
      dispatch(loadDesks(areaId)).unwrap(),
      dispatch(
        loadDeskBookingStatus({
          areaId,
          date,
        }),
      ).unwrap(),
    ]);

    return {
      desks,
      bookedById,
    };
  } catch (error) {
    handleError(error, dispatch);
    return rejectWithValue('Failed loading the desks');
  }
});

export const clearDeskSuggestions = createAction('desks/deskSuggestionsCleared', withPayloadType<void>());

export const clearDeskBookingStatus = createAction('desks/deskBookingStatusCleared', withPayloadType<void>());

export const clearDesks = createAction('desks/desksCleared', withPayloadType<void>());

export const suggestionErrorSet = createAction('desks/suggestionErrorSet', withPayloadType<SuggestionError>());

export const clearSuggestionError = createAction('desks/suggestionErrorClear', withPayloadType<void>());

export const resetSuggestedDesk = createAction('desks/resetSuggestedDesk', withPayloadType<void>());
